import type { CSSProperties } from 'react';

import { COLUMN_LIMIT } from '../../shared/queries.ts';
import type { IssueFilter } from '../../shared/queries.ts';
import { ISSUE_STATUSES } from '../../shared/schema.ts';
import type { IssueStatus } from '../../shared/schema.ts';
import { STATUS_LABELS } from '../format.ts';
import { useLive } from '../live.ts';
import type { IssueCard } from '../types.ts';
import { IssueRow } from './IssueRow.tsx';
import { StatusMark } from './Marks.tsx';

/**
 * Five columns, five live queries: the list's own query with a status in front of it. Moving an
 * issue to Review is one write, and two columns change because two live results did.
 *
 * **A `status:` term in the search names which columns the board HAS**, rather than being ANDed
 * onto each column's own status. Narrowing the board is the useful reading of it — `status:review`
 * is "show me Review", not "show me a board whose other four columns are empty" — and it is also
 * the only affordable one: a column the search excludes holds nothing, so leasing it spends one of
 * the page's live queries (the board is five of them) to maintain an empty window.
 *
 * The filter still goes down to each column unchanged. `queries.ts`'s `issueConditions`
 * intersects it with the column's own status, so Review asks for `review` as ONE binding — which
 * is what it asks for unfiltered, and what keeps the five columns partitions of one pipeline.
 * Sending both as two equalities is what the engine refuses, and what used to leave every column
 * showing its pre-filter rows for good.
 */
export function Board({ project, filter, selectedId, onOpen, onSearchTag }: { project: string; filter: IssueFilter; selectedId: string | null; onOpen: (id: string) => void; onSearchTag: (name: string) => void }) {
  const asked = filter.filter(term => term.axis === 'status').map(term => term.value);
  const columns = asked.length ? ISSUE_STATUSES.filter(status => asked.includes(status)) : ISSUE_STATUSES;
  return (
    // The track count is the column count, so a narrowed board fills the width instead of leaving
    // four empty fifths of it.
    <section className="board" aria-label="Board" style={{ '--columns': columns.length } as CSSProperties}>
      {columns.map(status => <Column key={status} project={project} status={status} filter={filter} selectedId={selectedId} onOpen={onOpen} onSearchTag={onSearchTag} />)}
    </section>
  );
}

function Column({ project, status, filter, selectedId, onOpen, onSearchTag }: { project: string; status: IssueStatus; filter: IssueFilter; selectedId: string | null; onOpen: (id: string) => void; onSearchTag: (name: string) => void }) {
  const live = useLive<IssueCard[]>('issues', { project, limit: COLUMN_LIMIT, filter, status });
  const rows = live.data ?? [];
  return (
    <div className="column">
      <header className="column-head">
        <StatusMark status={status} />
        <strong>{STATUS_LABELS[status]}</strong>
        <span className="micro muted">{live.status === 'live' ? (rows.length >= COLUMN_LIMIT ? `${COLUMN_LIMIT}+` : rows.length) : '…'}</span>
      </header>
      <ul className="column-list">
        {rows.map(issue => <IssueRow key={issue.id} issue={issue} compact selected={issue.id === selectedId} onOpen={onOpen} onSearchTag={onSearchTag} />)}
      </ul>
      {/* A column that cannot open its query says so. Without this it keeps whatever rows it had
          and only its count goes quiet, which is how a refused query reads as a frozen board. */}
      {live.error && <p className="empty small">Could not open this column: {live.error.message}</p>}
      {!live.error && live.status === 'live' && !rows.length && <p className="empty small">Nothing here.</p>}
    </div>
  );
}
